import { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { WatchQueryFetchPolicy, useApolloClient } from '@apollo/client';

import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import {
  UPDATE_ACTIVITIES_COMPONENT,
  DELETE_ACTIVITIES_COMPONENT,
  USE_COMPONENT_TEMPLATE,
  interactiveLessonFragment,
  interactiveLessonSectionComponentFragment,
  interactiveLessonSectionFragment,
  interactiveLessonSubSectionFragment,
} from './lesson-builder.graphql';
import type {
  UpdateActivitiesComponentMutation,
  UpdateActivitiesComponentMutationVariables,
} from './lesson-builder.graphql.generated';
import type {
  UpdateActivitiesComponentInput,
  DeleteActivitiesComponentInput,
  UseComponentTemplateInput,
} from '../../gql/schema-types';
import type {
  DeleteActivitiesComponentMutation,
  DeleteActivitiesComponentMutationVariables,
} from './lesson-builder.graphql.generated';
import { ComponentColumn } from './components/component.constants';
import type {
  UseComponentTemplateMutation,
  UseComponentTemplateMutationVariables,
} from './lesson-builder.graphql.generated';
import type { LessonActivitiesFragment } from './lesson-builder.graphql.generated';
import { useActivityBuilderContext } from './activity-builder.context';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import type { InteractiveLessonSectionComponentFragment } from './lesson-builder.graphql.generated';
import {
  getInteractiveContentType,
  getSortByIndex,
} from './activity-builder.utils';
import {
  addNewComponentToCache,
  addComponentToColumn,
  addComponentToSection,
  deleteComponentFromCache,
  reorderComponent,
  updateExistingComponent,
  updateSubActivityProperties,
  updateSectionOverviewActivityDetails,
  resetSectionInCache,
  addSubActivityToActivity,
  modifyActivitiesPublishChanges,
} from './activity-builder.apollo.utils';
import { useActivityBuilderTheme } from './theme/activity-builder-theme';
import {
  LESSON_ACTIVITIES,
  ActivitiesContentType,
  ActivitiesFontSize,
  LANGUAGE_CODE_EN_AU,
  LanguageCodes,
  ACTIVITIES_CONTENT_STATUS_DRAFT,
} from 'global/constants';
import type { InteractiveLessonSectionFragment } from './lesson-builder.graphql.generated';
import { AddComponentTemplate } from './components/components-menu/components-menu.constants';
import { getSubActivityInput } from './components/sub-activity/sub-activity.utils';
import { getColumn } from './components/columns/columns.utils';
import { useActivityBuilderApolloContext } from './activity-builder.apollo.context';
import customNanoId from 'lib/nanoid/nanoid';
import { getIdFromSlug } from 'lib/strings/strings';
import { interactiveAssessmentFragment } from './assessment/assessment-builder.graphql';
import type { AssessmentActivitiesFragment } from './assessment/assessment-builder.graphql.generated';
import { updateStimulusActivityInCache } from './activity-builder-stimulus.apollo';
import { useGetPublishedLessonActivitiesByVersion } from './graphql/use-get-published-lesson-activities-by-version';
import { useBatchComponentUpdateContext } from './components/shared/batch-component-update.context';
import { InquisitiveLanguageCodes } from 'global/graphql/scalars.type';
import type { GetPublishedLessonActivitiesByVersionQuery } from './graphql/get-published-lesson-activities-by-version.graphql.generated';
import { PUBLIC_ACTIVITIES } from 'global/public-routes.constants';
import { useActivityBuilderRequest } from './activity-builder-request.apollo';
import { useGetDraftActivitiesFromCache } from './logic/hooks/use-get-draft-activities-from-cache';
import { useGetDraftLessonActivities } from './graphql/use-get-draft-lesson-activities';
import type { Pluck } from 'lib/gql/pluck';

export const useGetInteractiveLessonParams = () => {
  const { slug = '', userClassId = '' } = useParams<{
    slug: string;
    userClassId: string;
  }>();
  const contentId = getIdFromSlug(slug);
  const [params] = useSearchParams();
  const version = params.get('version') || undefined;
  const draftId = params.get('draftId') || '';
  const page = params.get('page') || '';
  const assignmentId = params.get('assignmentId') || undefined;
  const classId = parseInt(params.get('classId') ?? '', 10) || undefined;
  const lang = (params.get('lang') ||
    LANGUAGE_CODE_EN_AU) as InquisitiveLanguageCodes;
  const contentType = useMemo(() => getInteractiveContentType(), []);
  const isPublic = window.location.pathname.includes(PUBLIC_ACTIVITIES);

  return {
    contentId: Number(contentId),
    slug,
    userClassId: Number(userClassId),
    classId,
    page,
    version,
    draftId,
    contentType,
    lang,
    isPublic,
    assignmentId: Number(assignmentId),
  };
};

type CommonProps = {
  onError?: () => void;
};

type UpdateComponentTemplateProps = {
  onCompleted?: (data: UseComponentTemplateMutation) => void;
} & CommonProps;

export const useUpdateComponentTemplate = (
  props?: UpdateComponentTemplateProps
) => {
  const { contentId, contentType } = useGetInteractiveLessonParams();
  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const [createWithTemplate, { data, loading, error, client }] =
    useLoggingMutation<
      UseComponentTemplateMutation,
      UseComponentTemplateMutationVariables
    >(USE_COMPONENT_TEMPLATE, {
      fetchPolicy: 'no-cache',
      onCompleted: result => props?.onCompleted && props.onCompleted(result),
      onError: () => props?.onError && props.onError(),
    });

  const createComponent = useCallback(
    async (
      input: Omit<UseComponentTemplateInput, 'contentId' | 'contentType'>
    ) => {
      const requestId = createRequest(input.componentId);
      await createWithTemplate({
        variables: {
          input: {
            ...input,
            contentId,
            contentType,
          },
        },
      });

      clearRequest(requestId);

      modifyActivitiesPublishChanges({
        client,
        id: contentId,
        contentType,
      });
    },
    [
      contentId,
      createWithTemplate,
      contentType,
      client,
      createRequest,
      clearRequest,
    ]
  );

  return {
    createComponent,
    data,
    loading,
    error,
  };
};

type UpdateComponentProps = {
  onCompleted?: (data: UpdateActivitiesComponentMutation) => void;
} & CommonProps;

export const useUpdateComponent = (props?: UpdateComponentProps) => {
  const { contentId, contentType } = useGetInteractiveLessonParams();
  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const [addComponent, { data, loading, error, client }] = useLoggingMutation<
    UpdateActivitiesComponentMutation,
    UpdateActivitiesComponentMutationVariables
  >(UPDATE_ACTIVITIES_COMPONENT, {
    onCompleted: result => props?.onCompleted && props.onCompleted(result),
    onError: () => props?.onError && props.onError(),
  });

  const updateComponent = useCallback(
    (
      input: Omit<UpdateActivitiesComponentInput, 'contentId' | 'contentType'>,
      optimisticResponse?: UpdateActivitiesComponentMutation
    ) => {
      const requestId = createRequest(input.componentId);
      return addComponent({
        variables: {
          input: {
            ...input,
            contentId,
            contentType,
          },
        },
        optimisticResponse,
      })
        .then(() => {
          modifyActivitiesPublishChanges({
            client,
            id: contentId,
            contentType,
          });
        })
        .catch(() => {})
        .finally(() => {
          clearRequest(requestId);
        });
    },
    [contentId, addComponent, contentType, client, createRequest, clearRequest]
  );

  return {
    contentId,
    data,
    loading,
    error,
    updateComponent,
  };
};

export const useUpdateComponentModify = (
  props: UpdateComponentProps & {
    contentId: number;
    sectionId: string;
  }
) => {
  const { contentId, contentType } = useGetInteractiveLessonParams();
  const { interactiveSectionClone, setInteractiveSectionClone } =
    useActivityBuilderApolloContext();
  const { getSection } = useGetInteractiveLessonDataFromCache();

  const [addComponent, { data, loading, error, client }] = useLoggingMutation<
    UpdateActivitiesComponentMutation,
    UpdateActivitiesComponentMutationVariables
  >(UPDATE_ACTIVITIES_COMPONENT, {
    onCompleted: result => {
      if (interactiveSectionClone) {
        setInteractiveSectionClone(null);
      }
      props?.onCompleted && props.onCompleted(result);
    },
    onError: () => {
      if (interactiveSectionClone) {
        resetSectionInCache({
          client,
          section: interactiveSectionClone,
        });
      }
      props.onError?.();
    },
  });

  const updateComponent = useCallback(
    ({
      input,
      typename,
      options,
    }: {
      input: Omit<UpdateActivitiesComponentInput, 'contentId' | 'contentType'>;
      typename: string;
      options?: {
        updateCache?: boolean;
        doManualCacheUpdate: () => void;
      };
    }) => {
      if (!input.componentId) return;

      setInteractiveSectionClone(getSection(props.sectionId));

      if (options?.doManualCacheUpdate) {
        options.doManualCacheUpdate();
      } else if (input.componentData.properties) {
        updateExistingComponent({
          client,
          id: input.componentId,
          typename,
          componentData: input.componentData,
        });
      }

      if (input.componentData.sort) {
        reorderComponent({
          client,
          id: input.componentId,
          typename,
          sort: input.componentData.sort,
        });
      }

      if (input.subActivityDetails) {
        updateSubActivityProperties({
          client,
          id: input.componentId,
          typename,
          properties: input.subActivityDetails,
        });
      }

      if (
        input.componentData.properties?.activitySettings?.differentiation ||
        input.componentData.properties?.activitySettings?.checkPoint
      ) {
        updateSectionOverviewActivityDetails({
          client,
          sectionId: input.sectionId,
          differentiation:
            input.componentData.properties.activitySettings.differentiation ||
            null,
          checkPoint:
            input.componentData.properties.activitySettings.checkPoint || null,
        });
      }

      addComponent({
        fetchPolicy: options?.updateCache ? undefined : 'no-cache',
        ignoreResults: options?.updateCache ? false : true,
        variables: {
          input: {
            ...input,
            contentId,
            contentType,
          },
        },
      })
        .then(() => {
          modifyActivitiesPublishChanges({
            client,
            id: contentId,
            contentType,
          });
        })
        .catch(() => {});
    },
    [
      client,
      contentId,
      addComponent,
      getSection,
      setInteractiveSectionClone,
      props.sectionId,
      contentType,
    ]
  );

  return {
    updateComponent,
    data,
    loading,
    error,
  };
};

type GetInteractiveProps = {
  contentId?: number;
  classId: number | null;
  version?: string;
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
  language?: LanguageCodes;
  onComplete?: (res: GetPublishedLessonActivitiesByVersionQuery) => void;
};

export const useGetLessonActivities = ({
  contentId,
  classId,
  version,
  fetchPolicy = 'cache-first',
  skip,
  language,
  onComplete,
}: GetInteractiveProps) => {
  const { updateFontSize } = useActivityBuilderTheme();

  const {
    data: draftData,
    loading,
    error,
  } = useGetDraftLessonActivities({
    contentId,
    skip: !!(version || !contentId || skip),
    language,
  });

  const {
    data: publishedData,
    loading: publishedLoading,
    error: publishedError,
  } = useGetPublishedLessonActivitiesByVersion({
    fetchPolicy,
    skip: !version || skip,
    version: version || '',
    isSignedIn: true,
    classId,
    language,
    onCompleted: res => {
      onComplete?.(res);
      if (res.getPublishedLessonActivitiesByVersion.styles) {
        updateFontSize(
          res.getPublishedLessonActivitiesByVersion.styles
            .fontSize as ActivitiesFontSize
        );
      }
    },
  });

  return {
    data: version
      ? publishedData?.getPublishedLessonActivitiesByVersion
      : draftData,
    loading: version ? publishedLoading : loading,
    error: version ? publishedError : error,
  };
};

export const useGetInteractiveLessonDataFromCache = () => {
  const client = useApolloClient();

  return {
    getInteractiveContent: (
      contentId: number,
      contentType: ActivitiesContentType
    ) => {
      const data = client.readFragment<LessonActivitiesFragment>({
        id: client.cache.identify({
          __typename:
            contentType === LESSON_ACTIVITIES
              ? 'LessonActivities'
              : 'AssessmentActivities',
          id: contentId,
          language: LANGUAGE_CODE_EN_AU,
          version: ACTIVITIES_CONTENT_STATUS_DRAFT,
        }),
        fragment:
          contentType === LESSON_ACTIVITIES
            ? interactiveLessonFragment
            : interactiveAssessmentFragment,
        fragmentName:
          contentType === LESSON_ACTIVITIES
            ? 'LessonActivities'
            : 'AssessmentActivities',
      });
      return data;
    },
    getInteractiveAssessmentContent(id: number) {
      const data = client.readFragment<AssessmentActivitiesFragment>({
        id: client.cache.identify({
          __typename: 'InteractiveAssessment',
          id,
          language: LANGUAGE_CODE_EN_AU,
          version: ACTIVITIES_CONTENT_STATUS_DRAFT,
        }),
        fragment: interactiveAssessmentFragment,
        fragmentName: 'AssessmentActivities',
      });
      return data;
    },
    getSection: (
      sectionId: string,
      language: InquisitiveLanguageCodes = LANGUAGE_CODE_EN_AU,
      version: string = ACTIVITIES_CONTENT_STATUS_DRAFT
    ) => {
      let data = client.readFragment<InteractiveLessonSectionFragment>({
        id: client.cache.identify({
          __typename: 'ActivitiesSection',
          id: sectionId,
          language,
          version,
        }),
        fragment: interactiveLessonSectionFragment,
        fragmentName: 'InteractiveLessonSectionFragment',
      });
      if (!data) {
        // try and get the sub-section
        data = client.readFragment<InteractiveLessonSectionFragment>({
          id: client.cache.identify({
            __typename: 'ActivitiesSection',
            id: sectionId,
            language,
            version,
          }),
          fragment: interactiveLessonSubSectionFragment,
          fragmentName: 'InteractiveLessonSubSection',
        });
      }

      return data || undefined;
    },
    getComponent: (componentId: string, typename?: string) => {
      const data = client.readFragment<
        Pluck<InteractiveLessonSectionFragment, 'components'>
      >({
        id: client.cache.identify({
          __typename: typename || 'InteractiveLessonSectionComponent',
          id: componentId,
          language: LANGUAGE_CODE_EN_AU,
          version: ACTIVITIES_CONTENT_STATUS_DRAFT,
        }),
        fragment: interactiveLessonSectionComponentFragment,
        fragmentName: 'InteractiveLessonSectionComponent',
      });
      return data || undefined;
    },
  };
};

type DeleteComponentProps = {
  onCompleted?: () => void;
} & CommonProps;

export const useDeleteComponent = (props?: DeleteComponentProps) => {
  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const [deleteComponent, { data, loading, error, client }] =
    useLoggingMutation<
      DeleteActivitiesComponentMutation,
      DeleteActivitiesComponentMutationVariables
    >(DELETE_ACTIVITIES_COMPONENT, {
      onCompleted: () => props?.onCompleted && props.onCompleted(),
      onError: () => props?.onError && props.onError(),
    });
  const { contentId, contentType } = useGetInteractiveLessonParams();
  const handleDeleteComponent = useCallback(
    (
      input: Omit<DeleteActivitiesComponentInput, 'contentId' | 'contentType'>,
      optimisticResponse?: DeleteActivitiesComponentMutation
    ) => {
      const requestId = createRequest(input.componentId);
      return deleteComponent({
        variables: {
          input: {
            ...input,
            contentId,
            contentType,
          },
        },
        optimisticResponse,
      })
        .then(() => {
          modifyActivitiesPublishChanges({
            client,
            id: contentId,
            contentType,
          });
        })
        .catch(() => {})
        .finally(() => {
          clearRequest(requestId);
        });
    },
    [
      contentId,
      contentType,
      client,
      createRequest,
      clearRequest,
      deleteComponent,
    ]
  );

  return {
    deleteComponent: handleDeleteComponent,
    data,
    loading,
    error,
  };
};

export type ShowCaptionReturnProps = {
  showCaption: boolean;
  onChangeShowCaption: () => void;
};

export const useComponentMutations = ({
  sectionId,
  components,
}: {
  sectionId: string;
  components?: InteractiveLessonSectionComponentFragment[];
}) => {
  const client = useApolloClient();
  const { setActiveComponent, setComponentDrawerId } =
    useActivityBuilderContext();
  const { interactiveSectionClone, setInteractiveSectionClone } =
    useActivityBuilderApolloContext();
  const { contentId, contentType } = useGetInteractiveLessonParams();

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const { showError } = useSnackbar();

  const interactiveLesson = useGetDraftActivitiesFromCache({
    contentId,
    contentType,
  });
  const { getSection } = useGetInteractiveLessonDataFromCache();

  const { createComponent } = useUpdateComponentTemplate({
    onCompleted: () => {
      if (interactiveSectionClone) {
        setInteractiveSectionClone(null);
      }
    },
    onError: () => {
      if (interactiveSectionClone) {
        resetSectionInCache({
          client,
          section: interactiveSectionClone,
        });
      }
      showError('There was an error adding a new component');
    },
  });

  const { deleteComponent } = useDeleteComponent({
    onCompleted: () => {
      if (interactiveSectionClone) {
        setInteractiveSectionClone(null);
      }
      setActiveComponent(undefined);
    },
    onError: () => {
      if (interactiveSectionClone) {
        resetSectionInCache({
          client,
          section: interactiveSectionClone,
        });
      }
      showError('There was an error deleting a component');
    },
  });

  const handleReorderComponent = useCallback(
    (component: InteractiveLessonSectionComponentFragment, sort: string) => {
      updateComponentProperties({
        input: {
          sectionId,
          componentId: component.id,
          componentData: {
            sort,
          },
        },
        typename: component.__typename,
      });
    },
    [sectionId, updateComponentProperties]
  );

  const handleMoveUp = useCallback(
    (component: InteractiveLessonSectionComponentFragment, index: number) => {
      if (!components) return;
      handleReorderComponent(
        component,
        getSortByIndex({ items: components, index: index - 2 })
      );
    },
    [components, handleReorderComponent]
  );

  const handleMoveDown = useCallback(
    (component: InteractiveLessonSectionComponentFragment, index: number) => {
      if (!components) return;
      handleReorderComponent(
        component,
        getSortByIndex({ items: components, index: index + 1 })
      );
    },
    [components, handleReorderComponent]
  );

  const handleDelete = useCallback(
    (component: InteractiveLessonSectionComponentFragment) => {
      setInteractiveSectionClone(getSection(sectionId));
      deleteComponentFromCache({
        client,
        id: component.id,
        typename: component.__typename,
      });
      deleteComponent({
        sectionId,
        componentId: component.id,
      });
    },
    [client, getSection, sectionId, setInteractiveSectionClone, deleteComponent]
  );

  const handleAdd = useCallback(
    ({
      templateId,
      isSubActivity,
      optimisticComponent,
      columnComponentId,
      columnId,
      sort,
      componentData,
      defaultSubActivityData,
      stimulus,
    }: AddComponentTemplate & {
      columnComponentId?: string;
      columnId?: string;
      sort: string;
    }) => {
      const lesson = interactiveLesson;
      const section = getSection(sectionId);
      const parentSectionId = (
        section as unknown as Pluck<
          InteractiveLessonSectionFragment,
          'subSections'
        >
      )?.parentSectionId;
      const parentSection = parentSectionId
        ? getSection(parentSectionId)
        : undefined;

      if (!lesson || !section) return null;

      setInteractiveSectionClone(section);

      const { subActivityInput, optimisticResponse, activity } =
        getSubActivityInput(
          parentSection || section,
          defaultSubActivityData?.areInstructionsVisible
        );

      const id = customNanoId();
      const columns = (
        optimisticComponent.columns as ComponentColumn[] | undefined
      )?.map(c => {
        return {
          ...c,
          language: LANGUAGE_CODE_EN_AU,
          columnId: customNanoId(),
          components: c.components.map(comp => {
            return {
              ...comp,
              id: customNanoId(),
              sectionId: section.id,
              language: LANGUAGE_CODE_EN_AU as LanguageCodes,
            };
          }),
        };
      });

      const componentToAdd = {
        ...optimisticComponent,
        properties: {
          ...optimisticComponent.properties,
          stimulusId: stimulus?.id || null,
        },
        columns,
        id,
        ...(isSubActivity
          ? {
              subActivityProperties: {
                ...subActivityInput,
                ...optimisticResponse,
              },
            }
          : {}),
        sort,
        sectionId: section.id,
        language: LANGUAGE_CODE_EN_AU,
      };

      if (columns) {
        const allComponentsInColumns = columns.flatMap(c => c.components);
        allComponentsInColumns.forEach(component => {
          addNewComponentToCache({ client, component });
        });
      }

      addNewComponentToCache({ client, component: componentToAdd });

      if (columnId && columnComponentId) {
        const column = getColumn(section, columnComponentId, columnId);

        if (column) {
          addComponentToColumn({ client, component: componentToAdd, column });
        }
      } else {
        addComponentToSection({
          client,
          componentId: componentToAdd.id,
          componentTypename: componentToAdd.__typename,
          sectionId,
        });
      }

      if (isSubActivity && activity) {
        addSubActivityToActivity({
          client,
          id: activity.id,
          typename: activity.__typename,
          subActivity: componentToAdd,
        });
      }

      if (stimulus) {
        updateStimulusActivityInCache({
          client,
          componentId: componentToAdd.id,
          data: stimulus,
        });
      }

      createComponent({
        componentId: id,
        templateId,
        sectionId: section.id,
        sort,
        columnComponentId,
        columnId,
        stimulusId: stimulus?.id,
        ...(isSubActivity
          ? {
              subActivityDetails: subActivityInput,
            }
          : {}),
        columns: columns
          ? columns.map(c => {
              return {
                columnId: c.columnId,
                componentIds: c.components.map(comp => comp.id),
              };
            })
          : null,
        componentData,
      });

      // open the settings menu for the new component
      setActiveComponent({
        id,
        ...(columnId && columnComponentId
          ? {
              column: {
                id: columnId,
                componentId: columnComponentId,
              },
            }
          : {}),
        meta: { templateId: templateId },
      });
      setComponentDrawerId(id);
    },
    [
      createComponent,
      interactiveLesson,
      getSection,
      setActiveComponent,
      setComponentDrawerId,
      client,
      sectionId,
      setInteractiveSectionClone,
    ]
  );

  return {
    handleMoveUp,
    handleMoveDown,
    handleDelete,
    handleAdd,
    setActiveComponent,
    setComponentDrawerId,
  };
};
